import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { liveMarketService, searchService } from "../services/api";
import { formatGroupedNumber } from "../utils/currencyFormatter";
// Missing player photos render as the app-standard grey initials block
// (matches Search/Squad) via PlayerAvatar — no running-man, no remote
// placeholder (the old via.placeholder.com was dead → flicker).
import PlayerAvatar from "../components/common/PlayerAvatar";
import NationalityFlags from "../components/common/NationalityFlags";
import { resolveNationalities } from "../utils/nationalityFlags";
import toast from "react-hot-toast";
import MarketDesktopTabs from "../components/club/responsive/MarketDesktopTabs";
import { useMediaQuery } from "../hooks/useMediaQuery";
import MarketMobileTabs from "../components/club/responsive/MarketMobileTabs";

const Market = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [countries, setCountries] = useState([]);
  const [positions, setPositions] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [messageLoading, setMessageLoading] = useState({});
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  // Debounce timer in a ref so it survives re-renders (the old `let timerid`
  // was recreated every render, so clearTimeout never cancelled the pending
  // fetch → overlapping fetches + card churn).
  const searchTimer = useRef(null);
  const didMount = useRef(false);

  // Fetch the filter dropdowns once.
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Single source of player fetches on mount + when tab/filters change.
  useEffect(() => {
    fetchPlayers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  // Debounced refetch on search typing only — skip the initial mount so this
  // doesn't double-fire with the tab/filters effect above.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return undefined;
    }
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      fetchPlayers();
    }, 600);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Filter countries based on search
  useEffect(() => {
    if (countrySearch.trim()) {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [countrySearch, countries]);

  const fetchInitialData = async () => {
    try {
      const [countriesRes, positionsRes] = await Promise.all([
        searchService.getCountries(),
        searchService.getPositions(),
      ]);

      if (countriesRes.success) {
        setCountries(countriesRes.data);
        setFilteredCountries(countriesRes.data);
      }

      if (positionsRes.success) {
        const positionsArray = Array.isArray(positionsRes.data)
          ? positionsRes.data
          : positionsRes.data.positions || [];
        setPositions(positionsArray);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
    }
  };

  const fetchPlayers = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        status:
          activeTab === "all"
            ? "all"
            : activeTab === "for-sale"
            ? "for_sale"
            : "for_loan",
        search: searchQuery || undefined,
        // Load ALL listed players in one request (backend default page_size is
        // 10, max 100). Previously only the first 10 showed; the Live Market
        // is a small curated list so 100 covers it without a load-more.
        page_size: 100,
        ...filters,
      };

      const response = await liveMarketService.getLiveMarketPlayers(params);

      if (response.success) {
        setPlayers(response.data);
      } else {
        setError(response.error);
        toast.error(response.error);
      }
    } catch (error) {
      console.error("Error fetching players:", error);
      setError("Failed to fetch players");
      toast.error("Failed to fetch players");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchPlayers();
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({});
    setCountrySearch("");
  };

  const handleCountrySearch = (searchTerm) => {
    setCountrySearch(searchTerm);
    setIsCountryDropdownOpen(true);
  };

  const handleCountrySelect = (country) => {
    handleFilterChange("country", country.name);
    setCountrySearch(country.name);
    setIsCountryDropdownOpen(false);
  };

  const handleClearCountry = () => {
    handleFilterChange("country", "");
    setCountrySearch("");
  };

  const handleCloseModal = () => {
    setShowFilters(false);
    // Reset country search to match current filter
    setCountrySearch(filters.country || "");
  };

  const handleApplyFilters = () => {
    setShowFilters(false);
  };

  const handleMessageClub = async (player) => {
    setMessageLoading((prev) => ({ ...prev, [player.id]: true }));

    try {
      const response = await liveMarketService.createClubChat(player.club_id);
      if (response.success) {
        navigate("/inbox", {
          state: {
            channelId: response.channelId,
            channelType: response.channelType,
          },
        });
      } else {
        toast.error(response.error || "Failed to create chat");
      }
    } catch (error) {
      console.error("Error creating club chat:", error);
      const errorMessage =
        error?.response?.data?.non_field_errors ||
        error?.response?.data?.message ||
        "Failed to create chat";
      toast.error(errorMessage);
    } finally {
      setMessageLoading((prev) => ({ ...prev, [player.id]: false }));
    }
  };

  const getPlayerType = (player) => {
    if (player.is_available_for_sale) return "FOR SALE";
    if (player.is_available_for_loan) return "FOR LOAN";
    return "FOR SALE"; // fallback
  };

  // Grouped by the platform's one helper instead of a bare toLocaleString(),
  // which followed the VIEWER's locale — the same fee read "€ 1,200,000" in
  // New York and "€ 1.200.000" in Berlin. Now "€ 1'200'000" for everyone
  // (Marin, 2026-08-02).
  const getTransferFee = (player) => {
    if (player.is_available_for_sale && player.transfer_fee) {
      return `€ ${formatGroupedNumber(player.transfer_fee)}`;
    }
    if (player.is_available_for_loan && player.loan_fee) {
      return `€ ${formatGroupedNumber(player.loan_fee)}`;
    }
    return "€ N/A";
  };


  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return "N/A";
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  return (
    <div className=" mx-auto pb-6">
      <div className="px-4 md:px-12 py-4 border border-[#E5E6E8]">
        <h1 className="text-base md:text-xl text-[#202630] font-bold">
          Live Market
        </h1>
      </div>
      {/* Tabs and Search */}

      {isMobile ? (
        <MarketMobileTabs
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onFilterClick={() => setShowFilters(true)}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
      ) : (
        <MarketDesktopTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          handleSearch={handleSearch}
          setShowFilters={setShowFilters}
        />
      )}

      {/* Filters Modal */}
      {showFilters && (
        <>
          {/* Modal Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={handleCloseModal}
          />

          {/* Modal Content */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-semibold text-gray-900">
                    Filters
                  </h2>
                  <button
                    onClick={handleCloseModal}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Country Filter - Like Organizer Page */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-[#D2D4D8] rounded-lg text-sm placeholder:text-[#1E1E1ECC] text-[#1E1E1ECC] focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Country"
                        value={countrySearch}
                        onChange={(e) => {
                          handleCountrySearch(e.target.value);
                        }}
                        onClick={() => setIsCountryDropdownOpen(true)}
                      />
                      {filters.country && (
                        <button
                          type="button"
                          className="absolute right-8 top-1/2 -translate-y-1/2 p-1 hover:text-red-500"
                          onClick={handleClearCountry}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="#1E1E1ECC"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      )}
                      <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <button
                          type="button"
                          className="p-1"
                          onClick={() =>
                            setIsCountryDropdownOpen(!isCountryDropdownOpen)
                          }
                        >
                          <svg
                            className="w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="#1E1E1ECC"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {isCountryDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <button
                              key={country.id}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                              onClick={() => handleCountrySelect(country)}
                            >
                              {country.image_path && (
                                <img
                                  src={country.image_path}
                                  alt={country.name}
                                  className="w-5 h-4 object-cover"
                                />
                              )}
                              <span>{country.name}</span>
                              {country.fifa_name && (
                                <span className="text-xs text-gray-500">
                                  ({country.fifa_name})
                                </span>
                              )}
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-2 text-sm text-gray-500">
                            No countries found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Position Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Position
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-[#D2D4D8] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={filters.position || ""}
                    onChange={(e) =>
                      handleFilterChange("position", e.target.value)
                    }
                  >
                    <option value="">All Positions</option>
                    {positions.map((position) => (
                      <option key={position.id} value={position.name}>
                        {position.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sort Order */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-[#D2D4D8] rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                    value={filters.order || "name_asc"}
                    onChange={(e) =>
                      handleFilterChange("order", e.target.value)
                    }
                  >
                    <option value="name_asc">Name (A-Z)</option>
                    <option value="name_desc">Name (Z-A)</option>
                  </select>
                </div>
              </div>

              <div className="p-6 border-t border-gray-200 flex gap-3">
                <button
                  onClick={handleClearFilters}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Clear All
                </button>
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Player Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4 md:px-12 bg-[#F8F9FB] pt-6">
        {loading && players.length === 0 ? (
          // Skeletons on FIRST load only; on background refetch (tab/search/
          // filter change) keep the existing cards mounted to avoid flicker.
          Array.from({ length: 6 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl border border-[#D2D4D880] overflow-hidden animate-pulse"
            >
              <div className="p-6">
                <div className="mb-3">
                  <div className="h-6 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="flex mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  <div className="ml-4">
                    <div className="h-5 bg-gray-200 rounded w-32 mb-2"></div>
                  </div>
                </div>
                <div className="mb-4 bg-[#F7F7F7] p-2 rounded-lg">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-3 bg-gray-200 rounded w-full"
                    ></div>
                  ))}
                </div>
                <div className="w-full h-10 bg-gray-200 rounded-lg mt-6"></div>
              </div>
            </div>
          ))
        ) : error ? (
          // Error state
          <div className="col-span-3 text-center py-12">
            <p className="text-red-500">{error}</p>
            <button
              onClick={() => fetchPlayers()}
              className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
            >
              Try Again
            </button>
          </div>
        ) : players.length === 0 ? (
          // Empty state
          <div className="col-span-3 text-center py-12">
            <p className="text-gray-500">No players found</p>
          </div>
        ) : (
          // Players list
          players.map((player) => {
            const playerType = getPlayerType(player);
            const transferFee = getTransferFee(player);
            const age = getAge(player.date_of_birth);

            return (
              <div
                key={player.id}
                className="bg-white rounded-xl border border-[#D2D4D880] overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="mb-3">
                    <div>
                      <div className="flex items-center gap-5">
                        <span className="text-xl font-bold text-[#242426]">
                          {player.position_name}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded ${
                            playerType === "FOR SALE"
                              ? "bg-[#049669] text-white"
                              : "bg-[#FF9500] text-white"
                          }`}
                        >
                          {playerType}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex mb-4">
                    <div className="flex items-center gap-3 cursor-pointer" onClick={()=>{navigate(`/search/player/${player.id}`)}}>
                      {/* App-standard missing-photo look: grey block + initials
                          (matches Search/Squad) — no running-man, no remote. */}
                      <PlayerAvatar
                        src={player.player_image}
                        name={player.name}
                        className="w-12 h-12 rounded-lg"
                      />
                      <div className="-mt-1">
                        <h3 className="font-medium text-[#242426] text-[18px]">
                          {player.display_name || player.name}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 bg-[#F7F7F7] p-2 rounded-lg flex justify-between items-center">
                    <div className="text-sm text-[#1A212F] font-medium">
                      Transfer fee
                    </div>
                    <div className="font-medium text-[#000000] text-sm">
                      {transferFee}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#242426B2]">
                        Current Club
                      </span>
                      <div className="flex items-center gap-2">
                        {player.club_logo && (
                          <img
                            src={player.club_logo}
                            alt={player.club}
                            className="w-5 h-5"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <span className="text-xs text-[#242426] font-medium">
                          {player.club || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#242426B2]">
                        Nationality
                      </span>
                      <div className="flex items-center gap-2">
                        {/* All citizenships (L-FE-1): ordered list from the backend, else the single field as before */}
                        <NationalityFlags nationalities={player} className="w-5 h-4 object-cover" wrapperClassName="flex flex-wrap items-center gap-1" />
                        <span className="text-xs text-[#242426] font-medium">
                          {resolveNationalities(player).map((n) => (typeof n === "string" ? n : n?.name)).filter(Boolean).join(" / ") || (typeof player.nationality === "string" ? player.nationality : player.nationality?.name) || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-xs text-[#242426B2]">Age</span>
                      <span className="text-xs text-[#242426] font-medium">
                        {age}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleMessageClub(player)}
                    disabled={messageLoading[player.id]}
                    className="w-full mt-6 py-2.5 text-center text-sm font-medium text-[#1E1E1E] border border-[#E6E6E6] rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {messageLoading[player.id] ? (
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-[#1E1E1E] rounded-full animate-spin"></div>
                        Loading...
                      </div>
                    ) : (
                      "Message Club"
                    )}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Market;
